import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AtokuUser } from '../models/atoku-user.model';
import { CalendarBooking } from '../models/atoku-calendar.model';
import {
    AtokuCategory,
    AtokuCategoryUpsertRequest,
    AtokuSvgLibrarySearchRequest,
    AtokuSvgLibrarySearchResponse
} from '../models/atoku-category.model';

export interface AtokuEquipment {
    id: string | number;
    name: string;
    description: string;
    totalAmount: number;
    bookedLast30Days: number;
    price: number;
}

export interface AtokuBlockOutEquipmentRequest {
    equipmentId: string;
    name: string;
    quantity: number;
}

export interface AtokuCreateBlockOutRequest {
    title: string;
    reason: string;
    startDate: string;
    endDate: string;
    spaceCode: string;
    subSpaceCodes: string[];
    equipment: AtokuBlockOutEquipmentRequest[];
    actionedByName?: string;
    actionedByEmail?: string;
    blocked: true;
}

export interface AtokuStation {
    id: string | number;
    name: string;
    category: string;
    price: number;
    spaceCode?: string;
    subSpaceCode?: string;
}

export interface AtokuDashboardSummary {
    totalUsers: number;
    avgTimeBooked: string;
    mostBookedCategory: string;
    mostBookedSpace: string;
}

export interface AtokuPeakActivity {
    peakTimes: string[];
    peakDays: string[];
}

export interface AtokuPricingProfile {
    id: number;
    code: string;
    name: string;
    description: string;
    [key: string]: unknown;
}

export interface AtokuPricingProfileLookup {
    code: string;
    name: string;
}

export interface AtokuUpsertEquipmentRequest {
    id?: string | number;
    name?: string;
    equipmentName?: string;
    totalAmount?: number;
    bookedLast30Days?: number;
    type?: 'equipment' | 'storage';
    [key: string]: unknown;
}

export interface AtokuCreatePricingProfileRequest {
    code?: string;
    name?: string;
    description?: string;
    Code?: string;
    Name?: string;
    Description?: string;
    [key: string]: unknown;
}

export interface AtokuPricingProfileItemPriceRequest {
    id: string;
    price: number;
    currencyCode?: string;
    unit?: number;
}

export interface AtokuUpsertPricingProfilePricesRequest {
    PricingProfileCode: string;
    Storage: AtokuPricingProfileItemPriceRequest[];
    Equipment: AtokuPricingProfileItemPriceRequest[];
    SubSpaces: AtokuPricingProfileItemPriceRequest[];
}

export interface AtokuBookingConfig {
    bufferTimeHours: number;
    maximumLeadTimeHours: number;
    smsNotificationsEnabled: boolean;
    opsTeamCellphoneNumbers: string[];
    allowedTeamMembers: number;
    allowAdditionalMembersToRequestAccess: boolean;
    allowAdditionalMembersToShareWithTeam: boolean;
    companyId: string;
    companyName: string;
    offerTextAccessPass: string;
    signupRouteAccessPass: string;
    allowedCardTypes: string[];
    clientReferenceFormat: string;
    refundMinimumNoticeHours: number;
    lateCancellationFeeAmount: number;
    lateCancellationFeeAmountType?: 'fixed' | 'percentage' | string;
    requiredDepositAmount?: number | null;
    requiredDepositAmountType?: 'fixed' | 'percentage' | string;
    [key: string]: unknown;
}

export interface AtokuBookingConfigUpdateRequest {
    bufferTimeHours: number;
    maximumLeadTimeHours: number;
    smsNotificationsEnabled: boolean;
    opsTeamCellphoneNumbers: string[];
    allowedTeamMembers: number;
    allowAdditionalMembersToRequestAccess: boolean;
    allowAdditionalMembersToShareWithTeam: boolean;
    offerTextAccessPass: string;
    signupRouteAccessPass: string;
    refundMinimumNoticeHours: number;
    lateCancellationFeeAmount: number;
    lateCancellationFeeAmountType: 'fixed' | 'percentage';
    requiredDepositAmount: number;
    requiredDepositAmountType: 'fixed' | 'percentage';
}

export interface AtokuTermsAndConditionsDocument {
    exists: boolean;
    documentId?: string | null;
    companyId: string;
    companyName: string;
    name: string;
    contentType: string;
    content: string;
    version: number;
    publishedOn?: string | null;
    upcomingLeasesUpdated?: number;
    updateMessage?: string | null;
}

export interface AtokuTermsAndConditionsUpdateRequest {
    name?: string;
    content: string;
}

export type AtokuEmailTemplateKey = 'approvalEmail' | 'bookingConfirmationEmail';

export interface AtokuEmailTemplateDocument {
    exists: boolean;
    documentId?: string | null;
    documentKey: AtokuEmailTemplateKey;
    companyId: string;
    companyName: string;
    name: string;
    subject: string;
    previewText: string;
    html: string;
    designJson: string;
    version: number;
    publishedOn?: string | null;
}

export interface AtokuEmailTemplateUpdateRequest {
    name?: string;
    subject?: string;
    previewText?: string;
    html: string;
    designJson: string;
}

export interface AtokuReportsResponse {
    generatedOn: string;
    defaultYear: number;
    availableYears: number[];
    pricingProfiles: AtokuReportPricingProfile[];
    kitchens: AtokuKitchenReport[];
    businessMixYears: AtokuBusinessMixYear[];
    storageAreas: AtokuStorageArea[];
}

export interface AtokuPaymentStatementLine {
    id?: string | number;
    paymentId?: string | number;
    payment_id?: string | number;
    reference?: string;
    paymentReference?: string;
    payment_reference?: string;
    transactionReference?: string;
    transaction_reference?: string;
    date?: string;
    paymentDate?: string;
    payment_date?: string;
    transactionDate?: string;
    transaction_date?: string;
    paidOn?: string;
    paid_on?: string;
    amount?: number | string;
    paymentAmount?: number | string;
    payment_amount?: number | string;
    amountIncl?: number | string;
    amount_incl?: number | string;
    total?: number | string;
    currency?: string;
    currencyCode?: string;
    currency_code?: string;
    payee?: string;
    customerName?: string;
    customer_name?: string;
    userName?: string;
    user_name?: string;
    companyName?: string;
    company_name?: string;
    description?: string;
    title?: string;
    bookingTitle?: string;
    booking_title?: string;
    type?: string;
    paymentType?: string;
    payment_type?: string;
    status?: string;
    [key: string]: unknown;
}

export interface AtokuReportPricingProfile {
    code: string;
    name: string;
}

export interface AtokuKitchenReport {
    spaceCode: string;
    spaceName: string;
    years: AtokuKitchenYear[];
}

export interface AtokuKitchenYear {
    year: number;
    months: AtokuKitchenMonth[];
}

export interface AtokuKitchenMonth {
    month: number;
    monthLabel: string;
    totalBookings: number;
    totalHours: number;
    totalBusinesses: number;
    pricingProfiles: AtokuKitchenPricingProfileMetric[];
    comments: string;
    weeks: AtokuKitchenWeek[];
}

export interface AtokuKitchenWeek {
    week: number;
    bookings: number;
    hours: number;
    businesses: number;
    pricingProfiles: AtokuKitchenPricingProfileMetric[];
}

export interface AtokuKitchenPricingProfileMetric {
    pricingProfileCode: string;
    pricingProfileName: string;
    businesses: number;
    hours: number;
}

export interface AtokuBusinessMixYear {
    year: number;
    months: AtokuBusinessMixMonth[];
}

export interface AtokuBusinessMixMonth {
    month: number;
    monthLabel: string;
    totalBookings: number;
    pricingProfiles: AtokuBusinessMixPricingProfileMetric[];
    unclassifiedBusinesses: number;
}

export interface AtokuBusinessMixPricingProfileMetric {
    pricingProfileCode: string;
    pricingProfileName: string;
    businesses: number;
    share: number;
}

export interface AtokuStorageArea {
    spaceCode: string;
    spaceName: string;
    items: AtokuStorageLine[];
}

export interface AtokuStorageLine {
    storageCode: string;
    storageName: string;
    totalUnits: number;
    inUseUnits: number;
    availableUnits: number;
    comments: string;
}

export interface AtokuAccessRequest {
    id: string | number;
    fullName: string;
    email: string;
    status: string;
    [key: string]: unknown;
}

export interface AtokuAccessRequestStatusUpdateRequest {
    status: 'approved' | 'granted' | 'declined';
    comment?: string;
    userEmail?: string;
    spaceCode?: string;
    startDate?: string;
    endDate?: string;
    title?: string;
}

export interface AtokuAccessCredentials {
  companyId: string;
  apiKey: string;
  siteId: string;
}

export interface AtokuCreateAccessRequest {
    userEmail: string;
    spaceCode: string;
    startDate: string;
    endDate: string;
    title: string;
    status?: 'approved' | 'granted' | 'declined' | 'pending';
    reason?: string;
    comment?: string;
}

export interface AtokuConsoleUserRequest {
    email: string;
}

export interface AtokuConsoleBlockUserRequest extends AtokuConsoleUserRequest {
    blocked?: boolean;
}

export interface AtokuCard {
    id: string;
    clientReference: string;
    userExternalId: string;
    firstName: string;
    lastName: string;
    email: string;
    userMobile: string;
    balance: number;
    deposit?: number | null;
    availableBalance: number;
    data?: string | null;
}

export interface AtokuCardDepositDeductionRequest {
    amount: number;
    currency?: string;
    reason: string;
    actionedBy?: string;
}

export interface AtokuEntriesCheckRequest {
    orderBy?: string;
    continuationToken?: string;
    userEmails?: string[];
}

export interface AtokuConsoleGetUsersRequest {
    continuationToken?: string;
    userEmails?: string[];
}

export interface AtokuSiteEntry {
    id?: string;
    user_id?: string;
    site_user_id?: string;
    local_date_time?: string;
    event_type?: string;
    door_name?: string;
    [key: string]: unknown;
}

export interface AtokuSiteEntriesResponse {
    items: AtokuSiteEntry[];
    count?: number;
    continuation_token?: string;
    [key: string]: unknown;
}

export interface AtokuConsoleActionResponse {
    message?: string;
    email?: string;
    user_id?: string;
    state?: string;
    blocked?: boolean;
    [key: string]: unknown;
}

export interface AtokuConsoleAccessCodeResponse extends AtokuConsoleActionResponse {
    pin?: string;
    key_id?: string;
    unblocked?: boolean;
    key_object?: unknown;
}

export interface AtokuConsoleMergedUser {
    user_id: string;
    site_user_id?: string;
    user_external_id?: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    client_reference?: string;
    user_mobile?: string;
    data?: string;
    subscription_state?: string;
    blocked?: boolean;
    [key: string]: unknown;
}

export interface AtokuConsoleMergedUsersResponse {
    items: AtokuConsoleMergedUser[];
    next_page_link?: string;
    count?: number;
}

interface NterBookingEquipmentDto {
    code?: string;
    name?: string;
    quantity?: number;
}

interface NterCalendarBookingDto {
    id?: number | string;
    title?: string;
    startDate?: string;
    endDate?: string;
    spaceCode?: string;
    subSpaceCode?: string;
    stationCode?: string;
    userName?: string;
    userEmail?: string;
    status?: string;
    blockedReason?: string;
    equipment?: NterBookingEquipmentDto[];
}

export interface AtokuConsolePricingProfileTypeResponse {
  id: string;
  code: string;
  name: string;
  description: string;
}

export interface AtokuConsoleCreateUserRequest extends Omit<AtokuAccessCredentials, 'siteId'> {
  companyId: string;
  apiKey: string;
  firstName: string,
  lastName: string,
  email: string,
  mobileNo: string,
  pricingProfileCode: string
}

export interface AtokuConsoleCreateUserResponse {
  status?: string;
  message?: string;
  result?: { link: string };
}

@Injectable({
    providedIn: 'root'
})
export class AtokuService {
    private readonly atokuApiHost = String(environment.AtokuCoreRoute ?? '').replace(/\/+$/, '');
    private readonly atokuAccessApiHost = String(environment.AtokuAccessRoute ?? '').replace(/\/+$/, '');
    private readonly companyCode = 'ML-SK';
    private readonly apiKey = 'b4828b77f6134b45bc1fbfe48a7d6aaf';

    constructor(private http: HttpClient) { }

    getDashboardSummary(): Observable<AtokuDashboardSummary> {
        return this.http.get<AtokuDashboardSummary>(
            `${this.atokuApiHost}/api/nter/dashboard/summary`,
            this.withAuthHeaders()
        );
    }

    getPeakActivity(): Observable<AtokuPeakActivity> {
        return this.http.get<AtokuPeakActivity>(
            `${this.atokuApiHost}/api/nter/dashboard/peak-activity`,
            this.withAuthHeaders()
        );
    }

    getTopEquipmentBooked(): Observable<{ name: string, hoursBooked: number }[]> {
        return this.http.get<{ name: string, hoursBooked: number }[]>(
            `${this.atokuApiHost}/api/nter/dashboard/top-equipment`,
            this.withAuthHeaders()
        );
    }

    getTopUsersByHours(): Observable<{ name: string, hoursBooked: number }[]> {
        return this.http.get<{ name: string, hoursBooked: number }[]>(
            `${this.atokuApiHost}/api/nter/dashboard/top-users`,
            this.withAuthHeaders()
        );
    }

    getRevenueChartData(): Observable<{ labels: string[], revenue: number[], hours: number[] }> {
        return this.http.get<{ labels: string[], revenue: number[], hours: number[] }>(
            `${this.atokuApiHost}/api/nter/dashboard/revenue-chart`,
            this.withAuthHeaders()
        );
    }

    getBookings(startDate: string, endDate?: string, activeOnly = false): Observable<CalendarBooking[]> {
        return this.http.get<NterCalendarBookingDto[]>(
            `${this.atokuApiHost}/api/nter/bookings/all`,
            this.withAuthHeaders({
                startDate,
                ...(endDate ? { endDate } : {}),
                ...(activeOnly ? { activeOnly: 'true' } : {})
            })
        ).pipe(
            map(bookings => (bookings ?? []).map(booking => this.toCalendarBooking(booking)))
        );
    }

    createBooking(booking: Partial<CalendarBooking>): Observable<CalendarBooking> {
        return this.http.post<CalendarBooking>(
            `${this.atokuApiHost}/api/nter/bookings/create`,
            booking,
            this.withAuthHeaders()
        );
    }

    createBlockOut(request: AtokuCreateBlockOutRequest): Observable<CalendarBooking> {
        return this.http.post<NterCalendarBookingDto>(
            `${this.atokuApiHost}/api/nter/bookings/create`,
            request,
            this.withAuthHeaders()
        ).pipe(
            map(booking => this.toCalendarBooking(booking))
        );
    }

    getUsers(): Observable<AtokuUser[]> {
        return this.http.get<AtokuUser[]>(
            `${this.atokuApiHost}/api/nter/users/all`,
            this.withAuthHeaders()
        );
    }

    getCards(): Observable<AtokuCard[]> {
        return this.http.get<AtokuCard[]>(
            `${this.atokuApiHost}/api/nter/cards/all`,
            this.withAuthHeaders()
        );
    }

    deductCardDeposit(cardId: string, request: AtokuCardDepositDeductionRequest): Observable<AtokuCard> {
        return this.http.post<AtokuCard>(
            `${this.atokuApiHost}/api/nter/cards/${encodeURIComponent(cardId)}/deposit/deduct`,
            {
                amount: request.amount,
                currency: request.currency ?? 'ZAR',
                reason: request.reason,
                actionedBy: request.actionedBy
            },
            this.withAuthHeaders()
        );
    }

    getEquipment(type?: 'equipment' | 'storage'): Observable<AtokuEquipment[]> {
        return this.http.get<AtokuEquipment[]>(
            `${this.atokuApiHost}/api/nter/equipment/all`,
            this.withAuthHeaders(type ? { type } : undefined)
        );
    }

    updateEquipment(id: string | number, data: AtokuUpsertEquipmentRequest): Observable<AtokuEquipment> {
        return this.http.put<AtokuEquipment>(
            `${this.atokuApiHost}/api/nter/equipment/update/${encodeURIComponent(String(id))}`,
            data,
            this.withAuthHeaders()
        );
    }

    deleteEquipment(id: string | number, type?: 'equipment' | 'storage'): Observable<void> {
        return this.http.delete<void>(
            `${this.atokuApiHost}/api/nter/equipment/delete/${encodeURIComponent(String(id))}`,
            this.withAuthHeaders(type ? { type } : undefined)
        );
    }

    createEquipment(data: AtokuUpsertEquipmentRequest): Observable<AtokuEquipment> {
        return this.http.post<AtokuEquipment>(
            `${this.atokuApiHost}/api/nter/equipment/create`,
            data,
            this.withAuthHeaders()
        );
    }

    getCategories(): Observable<AtokuCategory[]> {
        return this.http.get<AtokuCategory[]>(
            `${this.atokuApiHost}/api/nter/categories/all`,
            this.withAuthHeaders()
        );
    }

    createCategory(data: AtokuCategoryUpsertRequest): Observable<AtokuCategory> {
        return this.http.post<AtokuCategory>(
            `${this.atokuApiHost}/api/nter/categories/create`,
            data,
            this.withAuthHeaders()
        );
    }

    updateCategory(id: string | number, data: AtokuCategoryUpsertRequest): Observable<AtokuCategory> {
        return this.http.put<AtokuCategory>(
            `${this.atokuApiHost}/api/nter/categories/update/${encodeURIComponent(String(id))}`,
            data,
            this.withAuthHeaders()
        );
    }

    deleteCategory(id: string | number): Observable<void> {
        return this.http.delete<void>(
            `${this.atokuApiHost}/api/nter/categories/delete/${encodeURIComponent(String(id))}`,
            this.withAuthHeaders()
        );
    }

    searchSvgLibrary(request: AtokuSvgLibrarySearchRequest): Observable<AtokuSvgLibrarySearchResponse> {
        return this.http.post<AtokuSvgLibrarySearchResponse>(
            `${this.atokuApiHost}/api/nter/svg-library/search`,
            request,
            this.withAuthHeaders()
        );
    }

    getStations(): Observable<AtokuStation[]> {
        return this.http.get<AtokuStation[]>(
            `${this.atokuApiHost}/api/nter/stations/all`,
            this.withAuthHeaders()
        );
    }

    createStation(data: Partial<AtokuStation>): Observable<AtokuStation> {
        return this.http.post<AtokuStation>(
            `${this.atokuApiHost}/api/nter/stations/create`,
            data,
            this.withAuthHeaders()
        );
    }

    updateStation(id: string | number, data: Partial<AtokuStation>): Observable<AtokuStation> {
        return this.http.put<AtokuStation>(
            `${this.atokuApiHost}/api/nter/stations/update/${encodeURIComponent(String(id))}`,
            data,
            this.withAuthHeaders()
        );
    }

    deleteStation(id: string | number): Observable<void> {
        return this.http.delete<void>(
            `${this.atokuApiHost}/api/nter/stations/delete/${encodeURIComponent(String(id))}`,
            this.withAuthHeaders()
        );
    }

    getAccessRequests(): Observable<AtokuAccessRequest[]> {
        return this.http.get<AtokuAccessRequest[]>(
            `${this.atokuApiHost}/api/nter/accessrequests/all`,
            this.withAuthHeaders()
        );
    }

    updateAccessRequestStatus(id: string | number, data: AtokuAccessRequestStatusUpdateRequest): Observable<AtokuAccessRequest> {
        return this.http.patch<AtokuAccessRequest>(
            `${this.atokuApiHost}/api/nter/accessrequests/updatestatus/${encodeURIComponent(String(id))}`,
            data,
            this.withAuthHeaders()
        );
    }

    createAccessRequest(data: AtokuCreateAccessRequest): Observable<AtokuAccessRequest> {
        return this.http.post<AtokuAccessRequest>(
            `${this.atokuApiHost}/api/nter/accessrequests/create`,
            data,
            this.withAuthHeaders()
        );
    }

    getBookingConfig(): Observable<AtokuBookingConfig> {
        return this.http.get<AtokuBookingConfig>(
            `${this.atokuApiHost}/api/nter/bookingconfig/get`,
            this.withAuthHeaders()
        );
    }

    updateBookingConfig(data: AtokuBookingConfigUpdateRequest): Observable<AtokuBookingConfig> {
        return this.http.put<AtokuBookingConfig>(
            `${this.atokuApiHost}/api/nter/bookingconfig/update`,
            data,
            this.withAuthHeaders()
        );
    }

    getTermsAndConditions(): Observable<AtokuTermsAndConditionsDocument> {
        return this.http.get<AtokuTermsAndConditionsDocument>(
            `${this.atokuApiHost}/api/nter/bookingconfig/terms`,
            this.withAuthHeaders()
        );
    }

    updateTermsAndConditions(data: AtokuTermsAndConditionsUpdateRequest): Observable<AtokuTermsAndConditionsDocument> {
        return this.http.put<AtokuTermsAndConditionsDocument>(
            `${this.atokuApiHost}/api/nter/bookingconfig/terms`,
            data,
            this.withAuthHeaders()
        );
    }

    getEmailTemplate(templateKey: AtokuEmailTemplateKey): Observable<AtokuEmailTemplateDocument> {
        return this.http.get<AtokuEmailTemplateDocument>(
            `${this.atokuApiHost}/api/nter/bookingconfig/email/${encodeURIComponent(templateKey)}`,
            this.withAuthHeaders()
        );
    }

    updateEmailTemplate(
        templateKey: AtokuEmailTemplateKey,
        data: AtokuEmailTemplateUpdateRequest
    ): Observable<AtokuEmailTemplateDocument> {
        return this.http.put<AtokuEmailTemplateDocument>(
            `${this.atokuApiHost}/api/nter/bookingconfig/email/${encodeURIComponent(templateKey)}`,
            data,
            this.withAuthHeaders()
        );
    }

    getReports(): Observable<AtokuReportsResponse> {
        return this.http.get<AtokuReportsResponse>(
            `${this.atokuApiHost}/api/nter/reports/get`,
            this.withAuthHeaders()
        );
    }

    getPaymentStatement(startDate: string, endDate: string): Observable<AtokuPaymentStatementLine[]> {
        return this.http.get<AtokuPaymentStatementLine[]>(
            `${this.atokuApiHost}/api/nter/reports/payments`,
            this.withAuthHeaders({ startDate, endDate })
        );
    }

    getPricingProfiles(): Observable<AtokuPricingProfile[]> {
        return this.http.get<AtokuPricingProfile[]>(
            `${this.atokuApiHost}/api/nter/pricingprofile/all`,
            this.withAuthHeaders()
        );
    }

    getPricingProfileLookup(): Observable<AtokuPricingProfileLookup[]> {
        return this.http.get<AtokuPricingProfileLookup[]>(
            `${this.atokuApiHost}/api/nter/pricingprofile/lookup`,
            this.withAuthHeaders()
        );
    }

    createPricingProfile(data: AtokuCreatePricingProfileRequest): Observable<AtokuPricingProfile> {
        return this.http.post<AtokuPricingProfile>(
            `${this.atokuApiHost}/api/nter/pricingprofile/create`,
            data,
            this.withAuthHeaders()
        );
    }

    upsertPricingProfilePrices(data: AtokuUpsertPricingProfilePricesRequest): Observable<unknown> {
        return this.http.post<unknown>(
            `${this.atokuApiHost}/api/nter/pricingprofile/prices/upsert`,
            data,
            this.withAuthHeaders()
        );
    }

    checkSiteEntries(request?: AtokuEntriesCheckRequest): Observable<AtokuSiteEntriesResponse> {
        return this.http.post<AtokuSiteEntriesResponse>(`${this.atokuAccessApiHost}/entries/check`, {
            order_by: request?.orderBy,
            continuation_token: request?.continuationToken,
            user_emails: request?.userEmails
        }, this.withAuthHeaders());
    }

    getConsoleUsers(): Observable<AtokuConsoleMergedUsersResponse> {
        return this.http.get<AtokuConsoleMergedUsersResponse>(`${this.atokuAccessApiHost}/console/users/get`, this.withAuthHeaders());
    }

    subscribeConsoleUser(request: AtokuConsoleUserRequest): Observable<AtokuConsoleActionResponse> {
        return this.http.post<AtokuConsoleActionResponse>(`${this.atokuAccessApiHost}/console/users/subscribe`, {
            email: request.email
        }, this.withAuthHeaders());
    }

    unsubscribeConsoleUser(request: AtokuConsoleUserRequest): Observable<AtokuConsoleActionResponse> {
        return this.http.post<AtokuConsoleActionResponse>(`${this.atokuAccessApiHost}/console/users/unsubscribe`, {
            email: request.email
        }, this.withAuthHeaders());
    }

    generateConsoleAccessCode(request: AtokuConsoleUserRequest): Observable<AtokuConsoleAccessCodeResponse> {
        return this.http.post<AtokuConsoleAccessCodeResponse>(`${this.atokuAccessApiHost}/console/users/access-code`, {
            email: request.email
        }, this.withAuthHeaders());
    }

    blockConsoleUser(request: AtokuConsoleBlockUserRequest): Observable<AtokuConsoleActionResponse> {
        return this.http.post<AtokuConsoleActionResponse>(`${this.atokuAccessApiHost}/console/users/block`, {
            email: request.email,
            blocked: request.blocked
        }, this.withAuthHeaders());
    }

    deleteConsoleUser(request: AtokuConsoleUserRequest): Observable<AtokuConsoleActionResponse> {
        return this.http.post<AtokuConsoleActionResponse>(`${this.atokuAccessApiHost}/console/users/delete`, {
            email: request.email
        }, this.withAuthHeaders());
    }

  getPricingProfileTypes(request: Omit<AtokuAccessCredentials, 'siteId'>): Observable<AtokuConsolePricingProfileTypeResponse[]> {
    return this.http.get<AtokuConsolePricingProfileTypeResponse[]>(
      `${this.atokuApiHost}/api/nter/pricingprofile/all`,
      this.withAuthHeaders()
    );
  }

  createUser(request: AtokuConsoleCreateUserRequest): Observable<AtokuConsoleCreateUserResponse> {
    return this.http.post<AtokuConsoleCreateUserResponse>(`${this.atokuApiHost}/api/publicprofile/register`, request, this.withAuthHeaders());
  }

    private withAuthHeaders(params?: Record<string, string>): { headers: HttpHeaders, params?: Record<string, string> } {
        const headers = new HttpHeaders({
            'X-Api-Key': this.apiKey,
            'X-Company-Code': this.companyCode,
            'X-Company-Id': this.companyCode
        });

        return params ? { headers, params } : { headers };
    }

    private toCalendarBooking(booking: NterCalendarBookingDto): CalendarBooking {
        const start = booking.startDate ? new Date(booking.startDate) : new Date();
        const end = booking.endDate ? new Date(booking.endDate) : new Date(start.getTime() + (60 * 60 * 1000));
        const durationHours = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (60 * 60 * 1000)));
        const equipment = (booking.equipment ?? []).filter(item => !!item?.name);
        const firstEquipment = equipment[0];
        const userName = String(booking.userName ?? '').trim();
        const [firstName, ...surnameParts] = userName.split(' ').filter(Boolean);

        return {
            id: String(booking.id ?? ''),
            date: start,
            startHour: start.getHours(),
            durationHours,
            userName,
            firstName: firstName ?? '',
            lastName: surnameParts.join(' '),
            category: String(booking.status ?? ''),
            space: String(booking.stationCode ?? booking.subSpaceCode ?? ''),
            equipment: String(firstEquipment?.name ?? ''),
            equipmentAmount: Number(firstEquipment?.quantity ?? 0),
            equipmentList: equipment.map(item => String(item.name ?? '')),
            equipmentItems: equipment.map(item => ({
                code: item.code,
                name: item.name,
                quantity: item.quantity
            })),
            blockedReason: String(booking.blockedReason ?? '')
        } as CalendarBooking;
    }
}
